import * as Sentry from '@sentry/react';
import { ThemeProvider } from '@mui/material';
import { DYNAMIC_INVOICE_ID_TYPES } from '@weft/api-definition';
import React from 'react';
import { createPortal } from 'react-dom';
import { BrowserRouter as Router, Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { newVyTheme } from './assets/themes/new-vy-theme';
import { VyToastContainer } from './components/new-design/feedback/VyToastContainer';
import { SandboxDialog } from './components/utility/SandboxDialog';
import { useGlobalVariable } from './hooks/use-global-variable';
import { useIntegrations } from './hooks/use-integrations';
import { BillingCycleOverviewPage } from './pages/billing-cycle/BillingCycleOverviewPage';
import { BillingCyclePage } from './pages/billing-cycle/BillingCyclePage';
import { ContractPage } from './pages/contract/ContractPage';
import { ContractRevisePage } from './pages/contract/ContractRevisePage/ContractRevisePage';
import { ContractUpdatePage } from './pages/contract/ContractUpdatePage/ContractUpdatePage';
import { CustomerContractPage } from './pages/contract/CustomerContractPage';
import { CreateCustomerPage } from './pages/customers/CreateCustomerPage/CreateCustomerPage';
import { CustomerIdPage } from './pages/customers/CustomerIdPage/CustomerIdPage';
import { CustomerInfoViewEditPage } from './pages/customers/CustomerInfoViewEditPage/CustomerInfoViewEditPage';
import { CustomerInvoicesPage } from './pages/customers/CustomerInvoicesPage/CustomerInvoicesPage';
import { CustomerRealTimeActivityPage } from './pages/customers/CustomerRealTimeActivityPage/CustomerRealTimeActivityPage';
import { CustomersPage } from './pages/customers/CustomersPage/CustomersPage';
import { DataMeteringAdminPage } from './pages/data-metering/AdminPage';
import { DataMeteringPage } from './pages/data-metering/DataMeteringPage/DataMeteringPage';
import { EmptyPage } from './pages/empty/EmptyPage';
import { EventsPage } from './pages/events/EventsPage';
import { HomePage } from './pages/home/HomePage';
import { IntegrationProviderPage } from './pages/integrations/IntegrationProviderPage/IntegrationProviderPage';
import { IntegrationsPage } from './pages/integrations/IntegrationsPage/IntegrationsPage';
import { ApprovalFlowPage } from './pages/invoices/ApprovalFlowPage/ApprovalFlowPage';
import { CreateInvoicePage } from './pages/invoices/CreateInvoicePage/CreateInvoicePage';
import { CustomerInvoicePage } from './pages/invoices/CustomerInvoicePage/CustomerInvoicePage';
import { DynamicInvoiceViewPage } from './pages/invoices/DynamicInvoiceViewPage';
import { InvoicesPage } from './pages/invoices/InvoicesPage';
import { InvoiceViewPage } from './pages/invoices/InvoiceViewPage';
import { LightInvoiceViewPage } from './pages/invoices/LightInvoiceViewPage';
import { PaymentsPage } from './pages/payments/PaymentsPage/PaymentsPage';
import { CreatePlanPage } from './pages/plans/CreatePlanPage/CreatePlanPage';
import { PlansPage } from './pages/plans/PlansPage';
import { PlanViewPage } from './pages/plans/PlanViewPage';
import { CreateProductPage } from './pages/products/CreateProductPage/CreateProductPage';
import { ProductsPage } from './pages/products/ProductsPage/ProductsPage';
import { AnonymousCustomersReportPage } from './pages/reports/anonymous-customers-report-page/AnonymousCustomersReportPage';
import { CommitmentUsageReportPage } from './pages/reports/commitment-usage-report-page/CommitmentUsageReportPage';
import { ContractsReportPage } from './pages/reports/contracts-report-page/ContractsReportPage';
import { MonthlyClosureReportPage } from './pages/reports/monthly-closure-report-page/MonthlyclosureReportPage';
import { ProductUsageReportPage } from './pages/reports/product-usage-report-page/ProductUsageReportPage';
import { ReportsPage } from './pages/reports/reports-page/ReportsPage';
import { UnchargedUsageReportPage } from './pages/reports/uncharged-usage-report-page/UnchargedUsageReportPage';
import { SettingsPage } from './pages/settings/SettingsPage/SettingsPage';
import { LoginPage } from './pages/users/LoginPage/LoginPage';
import { RegisterConfirmationPage } from './pages/users/RegisterConfirmationPage/RegisterConfirmationPage';
import { RegisterPage } from './pages/users/RegisterPage/RegisterPage';
import { ResetPasswordPage } from './pages/users/ResetPasswordPage/ResetPasswordPage';
import { ProtectedRoute } from './router/ProtectedRoute';
import RootLayout from './router/RootLayout';
import { isNotProduction } from './utils/env-indicator';

export function App() {
  const [showSandboxDialog] = useGlobalVariable('showSandboxDialog');

  const UsageAdminPage = isNotProduction() ? <DataMeteringAdminPage /> : <EmptyPage />;
  useIntegrations();

  const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

  return (
    <Router>
      {showSandboxDialog && createPortal((
        <SandboxDialog />
      ), document.body)}

      <SentryRoutes>
        <Route
          path="login"
          element={(
            <ThemeProvider theme={newVyTheme}>
              <LoginPage />
            </ThemeProvider>
          )}
        />

        <Route
          path="register"
          element={(
            <ThemeProvider theme={newVyTheme}>
              <RegisterPage />
            </ThemeProvider>
          )}
        />

        <Route
          path="confirm"
          element={(
            <ThemeProvider theme={newVyTheme}>
              <RegisterConfirmationPage />
            </ThemeProvider>
          )}
        />
        <Route
          path="password/reset"
          element={(
            <ThemeProvider theme={newVyTheme}>
              <ResetPasswordPage />
            </ThemeProvider>
          )}
        />

        <Route element={(
          <ProtectedRoute>
            <ThemeProvider theme={newVyTheme}>
              <Outlet />
            </ThemeProvider>
          </ProtectedRoute>
        )}
        >
          <Route
            path=""
            element={(
              <Navigate
                to="home"
                replace
              />
            )}
          />

          <Route path='home'>
            <Route
              path="overview"
              element={<HomePage />}
            />

            <Route
              path="dashboard"
              element={<HomePage />}
            />

            <Route
              path=""
              element={(
                <Navigate
                  to="overview"
                  replace
                />
              )}
            />
          </Route>

          <Route path='integrations'>
            <Route
              path=''
              element={<IntegrationsPage />}
            />

            <Route
              path=':provider'
              element={<IntegrationProviderPage />}
            />
          </Route>

          <Route
            path="plans"
          >
            <Route
              path=''
              element={<PlansPage />}
            />
            <Route
              path=':planId'
              element={<PlanViewPage />}
            />

            <Route
              path="create"
              element={<CreatePlanPage />}
            />

            <Route path="contracts">
              <Route
                path=":contractId"
                element={<ContractPage />}
              />
            </Route>
          </Route>
          <Route path="invoices">
            <Route
              path=""
              element={(
                <Navigate
                  to="overview"
                  replace
                />
              )}
            />

            <Route
              path=":invoiceId/customer/:customerId"
              element={(
                <CustomerInvoicePage>
                  <Outlet/>
                </CustomerInvoicePage>
              )}
            >
              <Route
                path=""
                element={(
                  <Navigate
                    to="dynamic"
                    replace
                  />
                )}
              />

              <Route
                path="dynamic"
                element={(
                  <DynamicInvoiceViewPage
                    idType={DYNAMIC_INVOICE_ID_TYPES.Invoice}
                    doNotUsePageLayout
                  />
                )}
              />

              <Route
                path="preview"
                element={<LightInvoiceViewPage doNotUsePageLayout/>}
              />
            </Route>

            <Route path="overview">
              <Route
                path=''
                element={<InvoicesPage />}
              />

              <Route
                path='dynamic/:invoiceId'
                element={<DynamicInvoiceViewPage idType={DYNAMIC_INVOICE_ID_TYPES.Invoice} />}
              />

              <Route
                path='light/:invoiceId'
                element={<LightInvoiceViewPage />}
              />
            </Route>

            <Route
              path='monthly-closure'
              element={<BillingCyclePage />}
            />

            <Route
              path="payments"
              element={<PaymentsPage />}
            />

            <Route
              path="create"
              element={<CreateInvoicePage />}
            />

            <Route
              path='monthly-approval'
              element={<ApprovalFlowPage />}
            />
          </Route>

          <Route
            path="/products"
          >
            <Route
              path=""
              element={<ProductsPage />}
            />

            <Route
              path="create"
              element={<CreateProductPage />}
            />

            <Route
              path=":productId/edit"
              element={<CreateProductPage />}
            />
          </Route>

          <Route path="customers">
            <Route
              path=""
              element={<CustomersPage />}
            />
            <Route
              path="create"
              element={<CreateCustomerPage />}
            />

            <Route
              path=":customerId/invoices/:invoiceId"
              element={(
                <CustomerInvoicePage>
                  <Outlet/>
                </CustomerInvoicePage>
              )}
            >
              <Route
                path=""
                element={(
                  <Navigate
                    to="dynamic"
                    replace
                  />
                )}
              />

              <Route
                path="dynamic"
                element={(
                  <DynamicInvoiceViewPage
                    idType={DYNAMIC_INVOICE_ID_TYPES.Invoice}
                    doNotUsePageLayout
                  />
                )}
              />

              <Route
                path="preview"
                element={<LightInvoiceViewPage doNotUsePageLayout/>}
              />
            </Route>

            <Route
              path=":customerId"
              element={(
                <CustomerIdPage>
                  <Outlet />
                </CustomerIdPage>
              )}
            >
              <Route
                path=""
                element={(
                  <Navigate
                    to="realtime-activity"
                    replace
                  />
                )}
              />
              <Route
                path="realtime-activity"
                element={<CustomerRealTimeActivityPage />}
              />

              <Route
                path="invoices"
                element={<CustomerInvoicesPage />}
              />

              <Route
                path="contract"
                element={<CustomerContractPage />}
              />
              <Route
                path="settings"
                element={<CustomerInfoViewEditPage />}
              />
            </Route>
          </Route>

          <Route path="usage">
            <Route
              path='metering'
              element={<DataMeteringPage />}
            />
            <Route
              path='events'
              element={<EventsPage />}
            />
          </Route>

          <Route path="reports">
            <Route
              path=''
              element={<ReportsPage />}
            />
            <Route
              path='commitment-usage'
              element={<CommitmentUsageReportPage/>}
            />
            <Route
              path='commitment-usage/:reportId'
              element={<CommitmentUsageReportPage/>}
            />
            <Route
              path='product/:reportId'
              element={<ProductUsageReportPage/>}
            />
            <Route
              path='uncharged-usage/:reportId'
              element={<UnchargedUsageReportPage/>}
            />
            <Route
              path='commitment-usage'
              element={<CommitmentUsageReportPage/>}
            />
            <Route
              path='commitment-usage/:reportId'
              element={<CommitmentUsageReportPage/>}
            />

            <Route 
              path='monthly-closure'
              element={<MonthlyClosureReportPage />}
            />

            <Route
              path='monthly-closure/:reportId'
              element={<MonthlyClosureReportPage />}
            />

            <Route
              path='product/:reportId'
              element={<ProductUsageReportPage/>}
            />
            <Route
              path='uncharged-usage/:reportId'
              element={<UnchargedUsageReportPage/>}
            />
            <Route 
              path='contracts'
              element={<ContractsReportPage />}
            />

            <Route 
              path='contracts/:reportId'
              element={<ContractsReportPage />}
            />

            <Route 
              path='anonymous-customers'
              element={<AnonymousCustomersReportPage />}
            />

            <Route 
              path='anonymous-customers/:reportId'
              element={<AnonymousCustomersReportPage />}
            />

          </Route>
          <Route
            path="settings"
            element={<SettingsPage />}
          />
        </Route>

        <Route element={<ProtectedRoute><RootLayout /></ProtectedRoute>}>
          <Route path='plans'>
            <Route path="contracts">
              <Route
                path=":contractId/update"
                element={<ContractUpdatePage />}
              />
              <Route
                path=":contractId/revise"
                element={<ContractRevisePage />}
              />
            </Route>
          </Route>
          <Route path="invoices">
            <Route path='overview'>
              <Route
                path=':invoiceId'
                element={<InvoiceViewPage />}
              />
            </Route>

            <Route
              path=':invoiceId'
              element={<InvoiceViewPage />}
            />
            <Route
              path='billing-settings'
              element={<BillingCycleOverviewPage />}
            />
          </Route>
          <Route
            path="usage"
          >
            <Route
              path='metering/admin'
              element={UsageAdminPage}
            />
          </Route>

          <Route
            path='*'
            element={<EmptyPage />}
          />
        </Route>
      </SentryRoutes>
      <VyToastContainer />
    </Router>
  );
}

export default App;